import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'

import { useSetPointOfSaleUnlockPin } from '@/api/api-mutations'
import { useGetPointOfSaleUnlockPin } from '@/api/react-query'
import { Button } from '@/components/base/button'
import { Drawer } from '@/components/base/drawer'
import { Heading } from '@/components/base/heading'
import { Input } from '@/components/base/input'
import { Label } from '@/components/base/label'
import { Switch } from '@/components/base/switch'
import { toast } from '@/components/base/toast'
import { FormContainer, FormFieldGroup } from '@/components/form/form'
import { RouteModal, useRouteModal } from '@/components/route-modal'
import { useI18n } from '@/i18n/i18n-react'

export const Route = createFileRoute(
	'/_authenticated/settings/points-of-sale/devices/$pointOfSaleUuid/edit-unlock-pin'
)({
	component: RouteComponent,
	staticData: {
		crumb: ({ context: { L } }) => L.Settings.PointOfSale.UnlockPin.Title()
	}
})

function RouteComponent() {
	return (
		<RouteModal asDrawer>
			<EditUnlockPinForm />
		</RouteModal>
	)
}

function EditUnlockPinForm() {
	const { L } = useI18n()
	const { pointOfSaleUuid } = Route.useParams()
	const { handleSuccess } = useRouteModal()
	const unlockPin = useGetPointOfSaleUnlockPin({ pointOfSaleUuid })
	const setUnlockPin = useSetPointOfSaleUnlockPin()

	const [pin, setPin] = useState('')
	const [clearPin, setClearPin] = useState(false)
	const [saving, setSaving] = useState(false)

	// GET only returns `{ hasPin }` (never the PIN). Treat a failed fetch as unset so first-time
	// setup still works instead of wedging the form in a loading/disabled state.
	const hasPin = unlockPin.data?.hasPin ?? false
	const pinStatusLoading = unlockPin.isPending && !unlockPin.isError

	const handleSave = async () => {
		const body: { pin?: string | null } = {}

		if (clearPin) {
			body.pin = null
		} else if (pin !== '') {
			if (!/^\d{6}$/.test(pin)) {
				toast.error(L.Settings.PointOfSale.UnlockPin.PinInvalid())
				return
			}
			body.pin = pin
		}

		setSaving(true)
		try {
			const result = await setUnlockPin({ path: { pointOfSaleUuid }, body })

			if (result.isSuccess) {
				toast.success(L.Settings.PointOfSale.UnlockPin.SaveSuccess())
				handleSuccess()
				return
			}

			if (result.status === 400) {
				toast.error(L.Settings.PointOfSale.UnlockPin.PinInvalid())
			} else {
				toast.error(L.Error.SomethingWentWrong())
			}
		} finally {
			setSaving(false)
		}
	}

	return (
		<>
			<Drawer.Header>
				<Drawer.Title asChild>
					<Heading>{L.Settings.PointOfSale.UnlockPin.Title()}</Heading>
				</Drawer.Title>
			</Drawer.Header>
			<Drawer.Body className='flex-1 overflow-auto'>
				<FormContainer>
					<p className='txt-compact-small text-ui-fg-subtle'>
						{L.Settings.PointOfSale.UnlockPin.Description()}
					</p>
					<FormFieldGroup>
						<div className='flex flex-col gap-1.5'>
							<Label htmlFor='unlock-pin' size='small' weight='plus'>
								{L.Settings.PointOfSale.UnlockPin.NewPin()}
							</Label>
							<Input
								id='unlock-pin'
								inputMode='numeric'
								maxLength={6}
								autoComplete='off'
								value={pin}
								onChange={e => setPin(e.target.value.replace(/\D/g, ''))}
								placeholder={L.Settings.PointOfSale.UnlockPin.PinPlaceholder()}
								disabled={clearPin || pinStatusLoading}
							/>
							<span className='txt-compact-xsmall text-ui-fg-muted'>
								{hasPin
									? L.Settings.PointOfSale.UnlockPin.PinSet()
									: L.Settings.PointOfSale.UnlockPin.PinNotSet()}
							</span>
						</div>
						{hasPin ? (
							<div className='flex items-center justify-between'>
								<Label htmlFor='unlock-clear-pin' size='small'>
									{L.Settings.PointOfSale.UnlockPin.ClearPin()}
								</Label>
								<Switch
									id='unlock-clear-pin'
									checked={clearPin}
									onCheckedChange={value => {
										setClearPin(value)
										if (value) setPin('')
									}}
								/>
							</div>
						) : null}
					</FormFieldGroup>
				</FormContainer>
			</Drawer.Body>
			<Drawer.Footer>
				<div className='flex items-center justify-end gap-x-2'>
					<Drawer.Close asChild>
						<Button variant='secondary' size='small'>
							{L.General.Cancel()}
						</Button>
					</Drawer.Close>
					<Button onClick={() => void handleSave()} isLoading={saving} disabled={pinStatusLoading}>
						{L.Settings.PointOfSale.UnlockPin.Save()}
					</Button>
				</div>
			</Drawer.Footer>
		</>
	)
}
